import type { HeroCoverSettings } from '../types/reportTemplate';
import type { ReportTheme } from '../types/reportTheme';
import { DEFAULT_THEME } from '../types/reportTheme';

/** Parse #rgb / #rrggbb to r,g,b or null when invalid. */
function parseHex(hex: string): [number, number, number] | null {
  const raw = hex.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(raw)) {
    const r = parseInt(raw[0] + raw[0], 16);
    const g = parseInt(raw[1] + raw[1], 16);
    const b = parseInt(raw[2] + raw[2], 16);
    return [r, g, b];
  }
  if (/^[0-9a-f]{6}$/i.test(raw)) {
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    return [r, g, b];
  }
  return null;
}

/** Darken a hex colour toward black by `amount` ∈ [0, 1]. */
export function darkenHex(hex: string, amount: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const factor = 1 - Math.min(1, Math.max(0, amount));
  const [r, g, b] = rgb.map(v => Math.round(v * factor));
  return `#${[r, g, b].map(n => n.toString(16).padStart(2, '0')).join('')}`;
}

export interface ResolveHeroCoverBackgroundInput {
  settings?: HeroCoverSettings | null;
  theme: Pick<ReportTheme, 'primaryColor'>;
  /** Report-level hero image (site document) — fallback when template has no upload. */
  reportImageUrl?: string;
}

/**
 * Whether the author selected the GENERATED site-layout diagram as the
 * full-bleed hero background. The diagram is painted by the renderer over a
 * gradient base (so text tone / scrim follow the gradient path), NOT as a
 * `url(...)` image — keep this distinct from `usesImage`.
 */
export function resolveHeroCoverIsLayoutDiagram(settings?: HeroCoverSettings | null): boolean {
  return settings?.backgroundMode === 'layout-diagram';
}

/**
 * HERO-BG (#93) — is `imageUrl` an SVG upload the recolour layer can act on?
 *
 * This must recognise the SVG across EVERY URL shape the upload pipeline emits,
 * NOT just a bare `…/foo.svg?alt=media`. The reported bug was a silent gate miss:
 * the uploaded hero is served via the token-gated logo-media PROXY
 * (`uploadLogo` → `/api/storage/upload-file` → `buildLogoMediaUrl`), which emits
 *   `/api/storage/logo-file?path=logos%2Fuid%2Fabc.svg&token=…`
 * — here `.svg` lives inside the URL-encoded `path` query param and is followed
 * by `&token=…`, NOT by `?`/`#`/end. The previous gate `/\.svg(\?|#|$)/i`
 * therefore never matched, `recolorUploadedSvg` was false, the recolour layer
 * was never rendered, and the raw grey SVG showed through unchanged.
 *
 * Detection strategy (origin-independent, no network):
 *   1. `data:image/svg+xml` → SVG.
 *   2. The DECODED `path` query param (logo-media proxy + GCS `o/…` media URLs
 *      carry the real object name there) ends in `.svg` (with or without query).
 *   3. The decoded URL contains `.svg` at a path/extension boundary — followed
 *      by `?`, `#`, `&`, or end-of-string — so `…/foo.svg&token=…` matches too.
 */
export function isLikelySvgUrl(rawUrl?: string | null): boolean {
  const url = rawUrl?.trim() || '';
  if (!url) return false;
  // 1) data: URL with the SVG mime.
  if (/^data:image\/svg\+xml[;,]/i.test(url)) return true;
  // 2) The `path` query param (logo-media proxy + GCS media URLs) carries the
  //    real, possibly URL-encoded, object name. Decode it and check the ext.
  const pathParam = /[?&]path=([^&#]+)/i.exec(url)?.[1];
  if (pathParam) {
    let decoded = pathParam;
    try { decoded = decodeURIComponent(pathParam); } catch { /* keep raw */ }
    if (/\.svg(\?|#|$)/i.test(decoded)) return true;
  }
  // 3) `.svg` at any extension/query boundary in the (decoded) URL — covers the
  //    firebasestorage `o/…%2Ffoo.svg?alt=media` shape AND any literal
  //    `…/foo.svg&…` boundary the `?`/`#`/`$`-only regex missed.
  let decodedUrl = url;
  try { decodedUrl = decodeURIComponent(url); } catch { /* keep raw */ }
  return /\.svg(\?|#|&|$)/i.test(decodedUrl);
}

/**
 * Cleaned, ordered hex stops for a hero SVG / layout-diagram gradient fill.
 * Returns the stops only when at least two valid hex colours are supplied;
 * otherwise returns `[]` so callers fall back to the stroke / default fill.
 */
export function resolveHeroSvgGradientStops(settings?: HeroCoverSettings | null): string[] {
  const stops = (settings?.heroSvgGradient ?? [])
    .map((s) => s?.trim() || '')
    .filter((s) => parseHex(s) != null);
  return stops.length >= 2 ? stops : [];
}

/**
 * Build a top→bottom CSS `linear-gradient` from the resolved gradient base.
 *
 * `preferSvgGradient` (default true): a multi-stop `heroSvgGradient` (when
 * authored) takes precedence over the two-stop `gradientStart`/`gradientEnd`.
 * The LAYOUT-DIAGRAM path relies on this so the diagram base matches the SVG
 * fill stops. The UPLOADED-SVG background (Option A) sets it FALSE: there the
 * SVG fill is intentionally transparent (stroke-only line art over the cover
 * background), so the background gradient must come from the DEDICATED
 * `gradientStart`/`gradientEnd`, never `heroSvgGradient`.
 */
function gradientBackgroundString(
  input: ResolveHeroCoverBackgroundInput,
  preferSvgGradient = true,
): string {
  const { settings } = input;
  // A multi-stop SVG gradient (when authored) takes precedence over the
  // two-stop start/end so the diagram base matches the SVG fill stops.
  const svgStops = preferSvgGradient ? resolveHeroSvgGradientStops(settings) : [];
  if (svgStops.length >= 2) {
    const last = svgStops.length - 1;
    const parts = svgStops.map((c, i) => `${c} ${Math.round((i / last) * 100)}%`);
    return `linear-gradient(to bottom, ${parts.join(', ')})`;
  }
  // Gradient base follows the chosen theme/preset by default: the start stop is
  // the EFFECTIVE theme's primaryColor (preset-resolved by the caller), and the
  // end stop is a derived darken of it. A user-set gradientStart/gradientEnd
  // still wins (override precedence). The final fallback is the platform report-
  // theme default primary (DEFAULT_THEME.primaryColor) — NOT a hardcoded cyan —
  // so a theme missing primaryColor tracks the default rather than drifting.
  const start = settings?.gradientStart?.trim() || input.theme.primaryColor || DEFAULT_THEME.primaryColor;
  const end = settings?.gradientEnd?.trim() || darkenHex(start, 0.72);
  return `linear-gradient(to bottom, ${start} 0%, ${end} 100%)`;
}

/**
 * CSS `background` value for HeroImageCover.
 * Image mode prefers template upload, then report image, then gradient fallback.
 * Layout-diagram mode paints a gradient base (the diagram SVG is overlaid by
 * the renderer, not encoded here).
 */
export function resolveHeroCoverBackground(input: ResolveHeroCoverBackgroundInput): string {
  const { settings } = input;
  const templateImageUrl = settings?.imageUrl?.trim() || '';
  const reportImageUrl = input.reportImageUrl?.trim() || '';
  // Author explicitly chose the gradient (set backgroundMode === 'gradient').
  // Distinct from an unset/undefined `settings`, where there is no authored
  // template at all.
  const authorChoseGradient = settings?.backgroundMode === 'gradient';

  // Layout-diagram mode: the GENERATED boundary diagram is painted full-bleed
  // by the renderer. The background string is the gradient base it sits on, so
  // it never resolves to a `url(...)` image and text tone follows the gradient
  // contrast path below.
  if (resolveHeroCoverIsLayoutDiagram(settings)) {
    return gradientBackgroundString(input);
  }

  // Image precedence:
  //   1. A template hero upload (`heroCover.imageUrl`) — the explicit author
  //      choice — wins whenever it is set, even if backgroundMode is missing.
  //   2. Otherwise a report-supplied hero (report.heroImageUrl / included
  //      image-document) renders even when no template `heroCover` exists.
  //      This is the TC1 fix: historically `reportImageUrl` was passed as
  //      `undefined`, so report heroes silently fell through to the gradient.
  //      Callers only supply `reportImageUrl` when the report genuinely has a
  //      hero image, so honouring it cannot regress prior behaviour.
  //   3. When the author EXPLICITLY selected the gradient mode, the report
  //      fallback is suppressed so a deliberate gradient is not overridden by
  //      a stray site image.
  const imageUrl = templateImageUrl || (authorChoseGradient ? '' : reportImageUrl);
  if (imageUrl) {
    // HERO-BG (#93) Option A — "gradient = background only" for uploaded SVGs.
    // An uploaded SVG hero is line-art that is recoloured (stroke) and inlined
    // as a transparent-fill OVERLAY by HeroImageCover; it must NOT be the
    // `url(...)` background (that would paint an opaque box and the gradient
    // could never show through). So when the resolved hero image is an SVG
    // upload, paint the cover BACKGROUND from the dedicated background gradient
    // (`gradientStart`/`gradientEnd`, NOT `heroSvgGradient`) and let the SVG
    // overlay sit on top. Raster (PNG/JPG/WebP) uploads keep `url(...)`.
    if (isLikelySvgUrl(imageUrl)) {
      return gradientBackgroundString(input, /* preferSvgGradient */ false);
    }
    return `url(${imageUrl}) center/cover no-repeat`;
  }

  return gradientBackgroundString(input);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const parsed = parseHex(hex);
  if (!parsed) return null;
  const [r, g, b] = parsed;
  return { r, g, b };
}

function relativeLuminance(rgb: { r: number; g: number; b: number }): number {
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
}

/** WCAG 2.x contrast ratio — higher is more legible. */
export function wcagContrastRatio(a: string, b: string): number | null {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  if (!ra || !rb) return null;
  const la = relativeLuminance(ra);
  const lb = relativeLuminance(rb);
  const [lo, hi] = la < lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

function resolveGradientEndColor(
  input: ResolveHeroCoverBackgroundInput,
  preferSvgGradient = true,
): string {
  const { settings, theme } = input;
  // The bottom stop of an authored multi-stop SVG gradient sits where the
  // title block lands, so it drives the text tone when present — EXCEPT on the
  // uploaded-SVG background path (preferSvgGradient=false), where the cover
  // background is the dedicated `gradientStart`/`gradientEnd` and the SVG fill
  // is transparent, so the title sits over the start/end gradient instead.
  const svgStops = preferSvgGradient ? resolveHeroSvgGradientStops(settings) : [];
  if (svgStops.length >= 2) return svgStops[svgStops.length - 1];
  // Mirror gradientBackgroundString: theme-derived start (user override wins),
  // platform default primary as the final fallback (never a hardcoded cyan).
  const start = settings?.gradientStart?.trim() || theme.primaryColor || DEFAULT_THEME.primaryColor;
  return settings?.gradientEnd?.trim() || darkenHex(start, 0.72);
}

/**
 * Text/logo tone for the hero cover content block. Image backgrounds keep
 * white text (dark scrim). Gradient backgrounds pick black or white based on
 * contrast against the bottom gradient stop where the title block sits.
 */
export function resolveHeroCoverTextTone(input: ResolveHeroCoverBackgroundInput): 'on-dark' | 'on-light' {
  const { settings } = input;
  // Layout-diagram mode paints a gradient base (not an image), so its text
  // tone follows the gradient-contrast path below.
  if (resolveHeroCoverIsLayoutDiagram(settings)) {
    const end = resolveGradientEndColor(input);
    const whiteCr = wcagContrastRatio('#ffffff', end);
    const blackCr = wcagContrastRatio('#000000', end);
    if (whiteCr != null && blackCr != null) {
      return blackCr > whiteCr ? 'on-light' : 'on-dark';
    }
    return 'on-dark';
  }
  // Mirror resolveHeroCoverBackground's image precedence so the text tone /
  // scrim track whatever is actually painted as the background.
  const templateImageUrl = settings?.imageUrl?.trim() || '';
  const reportImageUrl = input.reportImageUrl?.trim() || '';
  const authorChoseGradient = settings?.backgroundMode === 'gradient';
  const imageUrl = templateImageUrl || (authorChoseGradient ? '' : reportImageUrl);
  if (imageUrl) {
    // HERO-BG (#93) Option A — an uploaded SVG paints a GRADIENT background
    // (not a `url(...)` image), so its text tone follows the gradient-contrast
    // path using the dedicated start/end gradient (NOT heroSvgGradient, which
    // is no longer the SVG fill). Raster uploads keep the on-dark photo tone.
    if (isLikelySvgUrl(imageUrl)) {
      const end = resolveGradientEndColor(input, /* preferSvgGradient */ false);
      const whiteCr = wcagContrastRatio('#ffffff', end);
      const blackCr = wcagContrastRatio('#000000', end);
      if (whiteCr != null && blackCr != null) {
        return blackCr > whiteCr ? 'on-light' : 'on-dark';
      }
      return 'on-dark';
    }
    return 'on-dark';
  }

  const end = resolveGradientEndColor(input);
  const whiteCr = wcagContrastRatio('#ffffff', end);
  const blackCr = wcagContrastRatio('#000000', end);
  if (whiteCr != null && blackCr != null) {
    return blackCr > whiteCr ? 'on-light' : 'on-dark';
  }
  return 'on-dark';
}

/** Scrim overlay matched to background mode and chosen text tone. */
export function resolveHeroCoverScrim(
  usesImage: boolean,
  textTone: 'on-dark' | 'on-light',
): string {
  if (usesImage) {
    return 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.30) 50%, rgba(0,0,0,0.05) 100%)';
  }
  if (textTone === 'on-light') {
    return 'linear-gradient(to top, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.12) 45%, rgba(255,255,255,0) 100%)';
  }
  return 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0) 100%)';
}
